import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import Button from '../../components/ui/Button';
import GroupForm from '../../components/forms/GroupForm';
import ExportModal from '../../components/ExportModal';
import DataTable, { Column } from '../../components/DataTable';
import AssignmentDrawer from '../../components/AssignmentDrawer';
import type { AssignmentValue } from '../../components/AssignmentDrawer';
import Modal from '../../components/Modal';
import { discoverFields, ExportableData, ExportField } from '../../utils/exportUtils';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import RoleAccessWidget from '../../components/RoleAccessWidget';
import { translateError } from '../../utils/errorMessages';
import SchedulePromptModal from '../../components/SchedulePromptModal';
import type {
  GroupSchedulePreview,
  ManagedGroupInfo,
  ScheduleAction,
  ScheduleSelection,
} from '../../types/schedulePrompt';

interface IGroup {
  _id: string;
  name: string;
  description: string;
  activityId?: number | null;
  managed?: ManagedGroupInfo | null;
}

type GroupOp = { action: ScheduleAction; memberIds: number[] };

type GroupPrompt = GroupOp & {
  preview: GroupSchedulePreview | null;
  error: string | null;
  /** Ops still to run after this one (drawer apply = adds, then removes). */
  rest: GroupOp[];
};

interface IMember {
  _id: string;
  name: string;
  description: string;
  color?: string;
  createdAt?: string;
  user?: {
    _id: string;
    name: string;
    email: string;
    admin?: boolean;
  };
  groups?: { _id: string; name: string }[];
}

const GroupEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [group, setGroup] = useState<IGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [groupMembers, setGroupMembers] = useState<ExportableData[]>([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Member management state
  const [allMembers, setAllMembers] = useState<IMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [groupMemberIds, setGroupMemberIds] = useState<Set<string>>(new Set());
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [prompt, setPrompt] = useState<GroupPrompt | null>(null);
  const [promptConfirming, setPromptConfirming] = useState(false);

  const navigate = useNavigate();

  const fetchMembers = useCallback(async () => {
    if (!id) return;
    setMembersLoading(true);
    try {
      // Managed groups are virtual, so membership comes from the group endpoint.
      const [{ data }, { data: roster }] = await Promise.all([
        api.members.list(),
        api.groups.members(id),
      ]);
      setAllMembers(data as IMember[]);
      setGroupMemberIds(new Set(roster.members.map(m => String(m._id))));
    } catch {
      // Non-critical, silently fail
    } finally {
      setMembersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const { data } = await api.groups.get(id!);
        setGroup(data as IGroup);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t));
        setLoading(false);
        if (err.response && err.response.status === 404) {
          navigate('/admin/groups');
        }
      }
    };

    if (id) {
      fetchGroup();
      fetchMembers();
    }
  }, [id, navigate, fetchMembers]);

  const membersInGroup = useMemo(() => {
    return allMembers.filter(m => groupMemberIds.has(String(m._id)));
  }, [allMembers, groupMemberIds]);

  const memberNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const m of allMembers) names[String(m._id)] = m.name;
    return names;
  }, [allMembers]);


  const memberColumns: Column<IMember>[] = useMemo(() => [
    { header: t('members.name'), key: 'name' },
    { header: t('common.description'), key: 'description' },
    {
      header: t('members.userCaregiver'),
      render: (member) => member.user ? member.user.name : 'N/A',
    },
  ], [t]);

  const submitHandler = async (data: { name: string; description: string; activityId: number | null }) => {
    setLoading(true);
    setError(null);

    try {
      await api.groups.update(id!, data);
      setLoading(false);
      addToast(t('groups.editTitle') + ' - ' + t('common.save') + '!', 'success');
      navigate('/admin/groups');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
    }
  };

  const handleExportClick = async () => {
    if (!id) return;

    setExportLoading(true);
    try {
      const [{ data: allMems }, { data: roster }] = await Promise.all([
        api.members.list(),
        api.groups.members(id),
      ]);
      const inGroup = new Set(roster.members.map(m => String(m._id)));
      const membersFiltered = (allMems as IMember[]).filter(member => inGroup.has(String(member._id)));
      setGroupMembers(membersFiltered);
      setShowExportModal(true);
    } catch (err: any) {
      console.error('Failed to fetch members for export:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const commitOp = async (op: GroupOp, selection: ScheduleSelection | null) => {
    // No selection = only the group membership changes (or the D9 cascade).
    const payload = {
      memberIds: op.memberIds,
      ...(selection ? { templateIds: selection.templateIds, eventIds: selection.eventIds } : {}),
    };
    if (op.action === 'add') await api.groups.addMembers(id!, payload);
    else await api.groups.removeMembers(id!, payload);
  };

  /** Run ops one prompt at a time (D6); adds with nothing to choose skip the prompt. */
  const runOps = async (ops: GroupOp[]): Promise<void> => {
    const [op, ...rest] = ops;
    if (!op) {
      setPrompt(null);
      fetchMembers();
      return;
    }
    setPrompt({ ...op, rest, preview: null, error: null });
    try {
      const { data } = await api.groups.schedulePreview(id!, op.action, op.memberIds);
      const empty = data.templates.length === 0 && data.events.length === 0 && data.charges.length === 0;
      if (op.action === 'add' && empty) {
        await commitOp(op, null);
        addToast(t('groups.addMembersSuccess'), 'success');
        return runOps(rest);
      }
      setPrompt(p => (p && p.action === op.action ? { ...p, preview: data } : p));
    } catch (err: any) {
      setPrompt(p => (p ? { ...p, error: translateError(err, t) } : p));
    }
  };

  const handlePromptConfirm = async (selection: ScheduleSelection | null) => {
    if (!prompt) return;
    setPromptConfirming(true);
    try {
      await commitOp(prompt, selection);
      if (prompt.action === 'add') {
        addToast(t('groups.addMembersSuccess'), 'success');
      } else {
        addToast(t('groups.removeMembersSuccess'), 'success');
        setSelectedMemberIds(new Set());
      }
      await runOps(prompt.rest);
    } catch (err: any) {
      const message = translateError(err, t, prompt.action === 'add' ? 'bulk.addToGroupError' : 'bulk.removeFromGroupError');
      addToast(message, 'error');
      // Enrolled members cannot leave the activity group: keep the reason in the prompt.
      if (err?.response?.data?.code === 'ACTIVITY_GROUP_MEMBER_ENROLLED') {
        const ids = String(err.response.data.params?.memberIds ?? '').split(',').filter(Boolean);
        const names = ids.map(mId => memberNames[mId] ?? `#${mId}`).join(', ');
        setPrompt(p => (p ? { ...p, error: names ? `${message} (${names})` : message } : p));
      }
    } finally {
      setPromptConfirming(false);
    }
  };

  const handlePromptClose = () => {
    setPrompt(null);
    // An earlier op of the queue may already have been applied.
    fetchMembers();
  };

  /** The drawer manages the whole roster, so diff both directions. */
  const handleApplyMembers = (next: AssignmentValue) => {
    if (!id) return;
    const before = new Set(membersInGroup.map(m => String(m._id)));
    const after = new Set(next.memberIds.map(String));
    const toAdd = [...after].filter(x => !before.has(x)).map(Number);
    const toRemove = [...before].filter(x => !after.has(x)).map(Number);
    setShowAddMembersModal(false);

    const ops: GroupOp[] = [];
    if (toAdd.length > 0) ops.push({ action: 'add', memberIds: toAdd });
    if (toRemove.length > 0) ops.push({ action: 'remove', memberIds: toRemove });
    if (ops.length > 0) runOps(ops);
  };

  const handleRemoveSelected = () => {
    if (!id || selectedMemberIds.size === 0) return;
    runOps([{ action: 'remove', memberIds: Array.from(selectedMemberIds).map(Number) }]);
  };

  const exportFields = useMemo<ExportField[]>(() => discoverFields(groupMembers), [groupMembers]);

  if (loading && !group) return <p>{t('groups.loadingGroup')}</p>;
  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-form-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>{t('groups.editTitle')}</h2>
        <Button variant="secondary" size="sm" onClick={handleExportClick} disabled={exportLoading}>
          {exportLoading ? t('common.loading') : t('export.exportMembers')}
        </Button>
      </div>
      <GroupForm
        initialData={group ? { name: group.name, description: group.description, activityId: group.activityId ?? null } : undefined}
        seedKey={group?._id}
        onSubmit={submitHandler}
        loading={loading && !!group}
        submitButtonText={t('groups.updateButton')}
      />
      <Link to="/admin/groups" className="form-link">{t('groups.goBack')}</Link>

      {/* Members Section */}
      <div style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>{t('groups.membersSection')}</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {selectedMemberIds.size > 0 && (
              <Button variant="danger" size="sm" onClick={handleRemoveSelected}>
                {t('groups.removeSelected')} ({selectedMemberIds.size})
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => setShowAddMembersModal(true)}>
              {t('groups.addMembers')}
            </Button>
          </div>
        </div>

        {group?.managed && (
          <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
            <span className="badge">{t('groups.managedBadge')}</span>{' '}
            {t(group.managed.kind === 'course' ? 'groups.managedCourseNote' : 'groups.managedActivityNote')}
          </p>
        )}

        {membersLoading ? (
          <p>{t('common.loading')}</p>
        ) : membersInGroup.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>{t('groups.noMembers')}</p>
        ) : (
          <DataTable
            data={membersInGroup}
            columns={memberColumns}
            selectable
            selectedIds={selectedMemberIds}
            onSelectionChange={setSelectedMemberIds}
          />
        )}
      </div>

      {/* Role Access */}
      {id && (
        <div style={{ marginTop: '2rem' }}>
          <CollapsibleWidget
            title={t('roles.entityAccess')}
            defaultExpanded={false}
            storageKey={`group-${id}-role-access`}
          >
            <RoleAccessWidget entityType="groups" entityId={id} />
          </CollapsibleWidget>
        </div>
      )}

      {/*
        Add / remove members. The drawer is the people-only variant — `groups` is
        deliberately not passed, because the parent here IS a group: there is no
        group to assign to a group, so the switcher and the provenance column
        would both be dead weight.
      */}
      <AssignmentDrawer
        isOpen={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        title={group ? `${t('groups.membersSection')} — ${group.name}` : t('groups.membersSection')}
        people={allMembers}
        value={{ memberIds: membersInGroup.map(m => m._id), groupIds: [] }}
        onApply={handleApplyMembers}
      />

      {/* D6: also apply to future events? (course group remove = un-enroll, D9) */}
      <SchedulePromptModal
        isOpen={!!prompt}
        onClose={handlePromptClose}
        action={prompt?.action ?? 'add'}
        title={t(
          group?.managed?.kind === 'course'
            ? (prompt?.action === 'remove' ? 'schedulePrompt.titleUnenroll' : 'schedulePrompt.titleEnroll')
            : (prompt?.action === 'remove' ? 'schedulePrompt.titleRemove' : 'schedulePrompt.titleAdd'),
        )}
        memberLabel={prompt ? t('schedulePrompt.membersLabel', {
          names: prompt.memberIds.map(mId => memberNames[String(mId)] ?? `#${mId}`).join(', '),
        }) : undefined}
        preview={prompt?.preview ?? null}
        previewLoading={!!prompt && !prompt.preview && !prompt.error}
        previewError={prompt?.error ?? null}
        selectable={prompt?.preview?.selectable ?? false}
        unenroll={prompt?.preview?.kind === 'course' && prompt.action === 'remove'}
        onConfirm={handlePromptConfirm}
        confirming={promptConfirming}
        memberNames={memberNames}
        paymentsLink="/admin/payments/overview"
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={groupMembers}
        filename={`group-${group?.name.replace(/\s+/g, '-').toLowerCase() || 'members'}-${new Date().toISOString().split('T')[0]}`}
        availableFields={exportFields}
        title={t('export.groupExportTitle')}
      />
    </div>
  );
};

export default GroupEditPage;
